import { useEffect, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { testimonials, siteStats, faqList } from '@/mocks/manga';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [formData, setFormData] = useState({ name: '', email: '', message: '', phone_alt: '' });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotValue = (form.querySelector('input[name="phone_alt"]') as HTMLInputElement)?.value?.trim() || '';

    if (honeypotValue) {
      setFormStatus('success');
      setFormData({ name: '', email: '', message: '', phone_alt: '' });
      return;
    }

    setFormStatus('submitting');
    setFormError('');

    try {
      const payload = new FormData(form);
      payload.delete('phone_alt');

      const response = await fetch('https://readdy.ai/api/form/d9ont58senbniolesihg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(payload as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormData({ name: '', email: '', message: '', phone_alt: '' });
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        setFormStatus('error');
        setFormError(serverMsg);
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请稍后重试');
    }
  };

  const aboutSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "关于亲亲漫画 - 免费全集下拉式在线漫画大全",
    "description": "亲亲漫画品牌介绍、口碑评价与联系方式。亲亲漫画按地域精选韩漫、日漫、国漫热门推荐，海量免费漫画在线全集下拉式阅读。",
    "url": "https://qinqinmanga.com/about",
    "isPartOf": {
      "@type": "WebSite",
      "name": "亲亲漫画",
      "url": "https://qinqinmanga.com/"
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqList.map((faq) => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer,
      },
    })),
  };

  return (
    <>
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutSchema) }}
    />
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
    />
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="pt-16">
        <div className="max-w-screen-2xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />

          <section className="py-8 md:py-12">
            <div className="max-w-4xl mx-auto">
              <h1 className="text-2xl md:text-3xl font-heading text-foreground-950 text-center mb-3">
                关于亲亲漫画
              </h1>
              <p className="text-sm text-foreground-500 text-center mb-8 md:mb-10 max-w-2xl mx-auto">
                致力于为广大漫画爱好者提供优质的免费在线阅读体验
              </p>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
                <div className="text-center p-5 rounded-lg bg-background-100 border border-background-200/70">
                  <div className="text-2xl md:text-3xl font-heading text-primary-500">{siteStats.totalManga.toLocaleString()}+</div>
                  <p className="text-xs text-foreground-500 mt-1">漫画资源</p>
                </div>
                <div className="text-center p-5 rounded-lg bg-background-100 border border-background-200/70">
                  <div className="text-2xl md:text-3xl font-heading text-accent-500">{siteStats.dailyUpdate}+</div>
                  <p className="text-xs text-foreground-500 mt-1">每日更新</p>
                </div>
                <div className="text-center p-5 rounded-lg bg-background-100 border border-background-200/70">
                  <div className="text-2xl md:text-3xl font-heading text-secondary-500">{(siteStats.totalUsers / 10000).toFixed(0)}万+</div>
                  <p className="text-xs text-foreground-500 mt-1">注册用户</p>
                </div>
                <div className="text-center p-5 rounded-lg bg-background-100 border border-background-200/70">
                  <div className="text-2xl md:text-3xl font-heading text-primary-500">99.9%</div>
                  <p className="text-xs text-foreground-500 mt-1">服务可用率</p>
                </div>
              </div>

              <article className="bg-background-100 rounded-lg p-5 md:p-8 mb-8 border border-background-200/70">
                <h2 className="text-lg font-heading text-foreground-950 mb-4">品牌介绍</h2>
                <div className="prose prose-sm max-w-none text-foreground-600 space-y-3">
                  <p>
                    亲亲漫画成立于2024年，是一个专注于<strong>韩漫、日漫、国漫</strong>热门推荐的综合在线漫画阅读平台。我们致力于为广大漫画爱好者提供海量<strong>免费漫画</strong>资源，打造最优质的<strong>在线阅读</strong>体验。
                  </p>
                  <p>
                    平台采用先进的<strong>下拉式</strong>阅读技术，告别传统翻页模式，让阅读更加流畅自然。无论是韩漫的唯美画风，日漫的热血战斗，还是国漫的东方韵味，都能在亲亲漫画找到你喜欢的作品。
                  </p>
                  <p>
                    我们的内容团队每日精选优质漫画资源，确保<strong>热门漫画</strong>第一时间同步更新。亲亲漫画目前已收录超过{siteStats.totalManga.toLocaleString()}部<strong>漫画大全</strong>资源，涵盖恋爱、热血、修仙、都市、玄幻等多种类型。
                  </p>
                  <p>
                    亲亲漫画坚持免费策略，所有<strong>全集漫画</strong>均可免费阅读，无需付费订阅。我们相信，好看的漫画应该让更多人看到，这也是亲亲漫画创立的初心。
                  </p>
                </div>
              </article>

              <section className="mb-8">
                <h2 className="text-lg font-heading text-foreground-950 mb-5 text-center">用户口碑</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {testimonials.map((t) => (
                    <div
                      key={t.id}
                      className="bg-background-100 rounded-lg p-4 md:p-5 border border-background-200/70"
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <span className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 text-primary-500">
                          <i className={`${t.avatar} text-sm`}></i>
                        </span>
                        <div>
                          <p className="text-sm font-semibold text-foreground-800">{t.name}</p>
                          <div className="flex items-center gap-0.5">
                            {Array.from({ length: t.rating }).map((_, i) => (
                              <span key={i} className="w-3.5 h-3.5 flex items-center justify-center text-accent-500">
                                <i className="ri-star-fill text-[10px]"></i>
                              </span>
                            ))}
                          </div>
                        </div>
                        <span className="ml-auto text-xs text-foreground-400">{t.date}</span>
                      </div>
                      <p className="text-sm text-foreground-600 leading-relaxed">{t.comment}</p>
                    </div>
                  ))}
                </div>
              </section>

              <section id="contact" className="bg-background-100 rounded-lg p-5 md:p-8 border border-background-200/70 mb-8">
                <h2 className="text-lg font-heading text-foreground-950 mb-2">联系我们</h2>
                <p className="text-sm text-foreground-500 mb-6">如有任何问题或建议，欢迎通过以下方式联系我们</p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                  <div className="flex items-start gap-3 p-4 rounded-lg bg-background-50 border border-background-200/70">
                    <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500 flex-shrink-0">
                      <i className="ri-mail-line"></i>
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-foreground-800 mb-0.5">电子邮箱</p>
                      <p className="text-sm text-foreground-500">contact@qinqinmanga.com</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-4 rounded-lg bg-background-50 border border-background-200/70">
                    <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100 text-accent-500 flex-shrink-0">
                      <i className="ri-customer-service-2-line"></i>
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-foreground-800 mb-0.5">在线客服</p>
                      <p className="text-sm text-foreground-500">工作日 9:00-18:00</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-4 rounded-lg bg-background-50 border border-background-200/70">
                    <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-500 flex-shrink-0">
                      <i className="ri-map-pin-line"></i>
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-foreground-800 mb-0.5">公司地址</p>
                      <p className="text-sm text-foreground-500">北京市朝阳区</p>
                    </div>
                  </div>
                </div>

                <form onSubmit={handleSubmit} data-readdy-form="">
                  <h3 className="text-base font-heading text-foreground-950 mb-4">发送消息</h3>

                  {formStatus === 'success' && (
                    <div className="mb-4 p-3 rounded-lg bg-secondary-100 border border-secondary-200 text-secondary-700 text-sm flex items-center gap-2">
                      <i className="ri-checkbox-circle-line"></i>
                      消息已成功发送！我们会尽快回复您。
                    </div>
                  )}

                  {formStatus === 'error' && (
                    <div className="mb-4 p-3 rounded-lg bg-primary-100 border border-primary-200 text-primary-700 text-sm flex items-center gap-2">
                      <i className="ri-error-warning-line"></i>
                      {formError || '提交失败，请稍后重试'}
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                      <input
                        id="contact-name"
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        className="w-full px-3.5 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all duration-200"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                      <input
                        id="contact-email"
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                        className="w-full px-3.5 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all duration-200"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                  </div>
                  <div className="mb-4">
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">消息内容</label>
                    <textarea
                      id="contact-message"
                      name="message"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-3.5 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all duration-200 resize-none"
                      placeholder="请输入您的消息内容（最多500字）"
                    />
                    <p className="text-xs text-foreground-400 mt-1 text-right">{formData.message.length}/500</p>
                  </div>

                  <div className="form-hp-wrapper">
                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      value={formData.phone_alt}
                      onChange={(e) => setFormData({ ...formData, phone_alt: e.target.value })}
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className="px-6 py-2.5 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-white font-medium text-sm rounded-lg transition-colors duration-200 cursor-pointer whitespace-nowrap flex items-center gap-2"
                  >
                    {formStatus === 'submitting' ? (
                      <>
                        <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                        发送中...
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-line"></i>发送消息
                      </>
                    )}
                  </button>
                </form>
              </section>

              <section className="mb-8">
                <h2 className="text-lg font-heading text-foreground-950 mb-5 text-center">常见问题</h2>
                <div className="max-w-3xl mx-auto space-y-3">
                  {faqList.map((faq, index) => (
                    <details key={index} className="group bg-background-100 rounded-lg border border-background-200/70 overflow-hidden">
                      <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                        <span className="text-sm font-medium text-foreground-800 pr-4">{faq.question}</span>
                        <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-foreground-400 group-open:rotate-180 transition-transform duration-200">
                          <i className="ri-arrow-down-s-line"></i>
                        </span>
                      </summary>
                      <div className="px-5 pb-4">
                        <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                      </div>
                    </details>
                  ))}
                </div>
              </section>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </div>
    </>
  );
}